import React, { useEffect, useState } from "react";
import { Users, Eye, Clock, TrendingDown } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { adminApi } from "@/lib/api";
import { eur } from "@/lib/format";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="border ee-hairline bg-[#1C1917] p-6" data-testid={testid}>
    <Icon className="ee-gold-text mb-4" strokeWidth={1.2} size={24} />
    <p className="font-display text-3xl text-[#FAFAF9]">{value}</p>
    <p className="text-xs text-[#A8A29E] uppercase tracking-[0.14em] mt-1">{label}</p>
  </div>
);

const RankList = ({ title, items, testid }) => (
  <div className="border ee-hairline bg-[#1C1917] p-6" data-testid={testid}>
    <p className="ee-overline mb-5">{title}</p>
    <ul className="space-y-3">
      {items?.length ? items.map((it) => (
        <li key={it.label} className="flex items-center justify-between text-sm">
          <span className="text-[#A8A29E] truncate pr-3">{it.label}</span>
          <span className="ee-gold-text">{it.count}</span>
        </li>
      )) : <li className="text-[#57534E] text-sm">No data yet</li>}
    </ul>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/analytics/overview", { params: { days: 30 } }).then((r) => setData(r.data)).catch(() => {});
    adminApi.get("/admin/orders", { params: { limit: 1 } }).then((r) => setRevenue(r.data.summary)).catch(() => {});
  }, []);

  if (!data) return <p className="ee-overline animate-pulse">Loading analytics…</p>;

  const t = data.totals;

  return (
    <div data-testid="admin-dashboard">
      <p className="ee-overline mb-2">Last 30 days</p>
      <h1 className="font-display text-4xl tracking-tight mb-8">Insights</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <Stat icon={Users} label="Visitors" value={t.visitors} testid="stat-visitors" />
        <Stat icon={Eye} label="Page Views" value={t.pageviews} testid="stat-pageviews" />
        <Stat icon={Clock} label="Avg. Time on Site" value={t.avg_time} testid="stat-avgtime" />
        <Stat icon={TrendingDown} label="Bounce Rate" value={`${t.bounce_rate}%`} testid="stat-bounce" />
      </div>

      {revenue && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <div className="border ee-hairline bg-[#1C1917] p-6">
            <p className="font-display text-3xl ee-gold-text">{eur(revenue.revenue)}</p>
            <p className="text-xs text-[#A8A29E] uppercase tracking-[0.14em] mt-1">Revenue (paid)</p>
          </div>
          <div className="border ee-hairline bg-[#1C1917] p-6">
            <p className="font-display text-3xl text-[#FAFAF9]">{revenue.paid_orders}</p>
            <p className="text-xs text-[#A8A29E] uppercase tracking-[0.14em] mt-1">Paid Orders</p>
          </div>
          <div className="border ee-hairline bg-[#1C1917] p-6">
            <p className="font-display text-3xl text-[#FAFAF9]">{revenue.total_orders}</p>
            <p className="text-xs text-[#A8A29E] uppercase tracking-[0.14em] mt-1">Total Orders</p>
          </div>
        </div>
      )}

      <div className="border ee-hairline bg-[#1C1917] p-6 mb-6" data-testid="traffic-chart">
        <p className="ee-overline mb-6">Traffic</p>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.timeseries}>
            <defs>
              <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#A8A29E" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#A8A29E" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
            <XAxis dataKey="date" tick={{ fill: "#57534E", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fill: "#57534E", fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0C0A09", border: "1px solid #292524", color: "#FAFAF9" }} />
            <Area type="monotone" dataKey="views" stroke="#D4AF37" fill="url(#gV)" strokeWidth={2} name="Views" />
            <Area type="monotone" dataKey="visitors" stroke="#A8A29E" fill="url(#gP)" strokeWidth={2} name="Visitors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        <RankList title="Top Countries" items={data.top_countries} testid="top-countries" />
        <RankList title="Top Cities" items={data.top_cities} testid="top-cities" />
        <RankList title="Top Pages" items={data.top_pages} testid="top-pages" />
        <RankList title="Referrers" items={data.top_referrers} testid="top-referrers" />
      </div>
    </div>
  );
}
